import { useWorkspaceInfoUsersById } from '@modules/workspace/providers/WorkspaceInfo';
import { Box, Popover, Stack, Typography, styled } from '@mui/material';
import classNames from 'classnames';
import cn from 'classnames';
import React, { useMemo, useState } from 'react';
import { useAuthContext } from 'src/auth/useAuthContext';
import { EmojiPopover } from 'src/components/EmojiPickerPopover';
import { ConversationReactions } from '../../../@types/conversation';

export const UsersReactionComponent = ({
  reaction,
}: { reaction: ConversationReactions }) => {
  const { usersById } = useWorkspaceInfoUsersById();

  const title = useMemo(
    () =>
      reaction.users
        .map((user) => usersById[user].name)
        .join(', ')
        .replace(/,([^,]*)$/, ' and$1'),
    [usersById, reaction.users],
  );

  return (
    <Stack direction="row" alignItems="center" gap={1} sx={{ px: 2, py: 0.5 }}>
      <em-emoji shortcodes={reaction.emoji} style={{ fontSize: '2em' }} />
      <Typography variant="caption">{title}</Typography>
    </Stack>
  );
};

export const ConversationMessageReactionItem = ({
  reaction,
  userReactions,
  sendReaction,
}: {
  reaction: ConversationReactions;
  userReactions: Record<string, boolean>;
  sendReaction: (emoji: string) => void;
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <StyledReaction
        className={classNames({
          active: !!userReactions[reaction.emoji],
        })}
        onClick={() => sendReaction(reaction.emoji)}
        aria-owns={open ? 'mouse-over-popover' : undefined}
        aria-haspopup="true"
        onMouseEnter={handlePopoverOpen}
        onMouseLeave={handlePopoverClose}
      >
        <em-emoji shortcodes={reaction.emoji} />
        <Typography variant="subtitle1">{reaction.users.length}</Typography>
      </StyledReaction>
      <Popover
        id="mouse-over-popover"
        sx={{
          pointerEvents: 'none',
        }}
        open={open}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        onClose={handlePopoverClose}
        disableRestoreFocus
      >
        <UsersReactionComponent reaction={reaction} />
      </Popover>
    </>
  );
};

type Props = {
  reactions: ConversationReactions[];
  sendReaction: (emoji: string) => void;
};

export const ConversationMessageReactions = ({
  reactions,
  sendReaction,
}: Props) => {
  const [isOpened, setIsOpened] = useState(false);

  const { user } = useAuthContext();

  const userReactions: Record<string, boolean> = useMemo(() => {
    if (!user?._id || !reactions?.length) return {};
    return reactions.reduce(
      (acc: Record<string, boolean>, { emoji, users }) => {
        if (users.includes(user?._id)) return { ...acc, [emoji]: true };
        return acc;
      },
      {},
    );
  }, [reactions, user?._id]);

  return (
    <StyledContainer>
      {reactions.map((reaction) => (
        <ConversationMessageReactionItem
          key={reaction.emoji}
          reaction={reaction}
          userReactions={userReactions}
          sendReaction={sendReaction}
        />
      ))}
      <Box className={cn('emoji-popover', { isOpened })}>
        <EmojiPopover
          onSelectEmoji={(emoji) => sendReaction(emoji.shortcodes)}
          arrow="left-center"
          icon="fluent:emoji-add-16-regular"
          isOpened={setIsOpened}
        />
      </Box>
    </StyledContainer>
  );
};

const StyledContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  margin: theme.spacing(0.5, 0),
  '& .emoji-popover': {
    visibility: 'hidden',
    '&.isOpened': {
      visibility: 'visible',
    },
  },
  '&:hover': {
    '& .emoji-popover': {
      visibility: 'visible',
    },
  },
}));

const StyledReaction = styled(Stack)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'row',
  gap: theme.spacing(0.5),
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(0.25, 1),
  cursor: 'pointer',
  backgroundColor: theme.palette.background.neutral,

  '&.active': {
    border: `1px solid ${theme.palette.primary.main}`,
  },
}));
